<template>
  <div>
    <b-form @submit.stop.prevent="onSubmit" class="login-box" data-cy="login-form">
      <b-form-checkbox
        v-model="qmsOtpMode"
        class="mb-3"
        :class="isDarkMode ? 'text-light' : ''"
        switch
        data-cy="login-qms-otp-mode"
      >
        QMS login (OTP)
      </b-form-checkbox>
      <b-form-group id="group-mobile" label label-for="input-mobile" description>
        <b-form-input
          id="input-mobile"
          v-model="form.mobile"
          type="text"
          inputmode="numeric"
          required
          placeholder="Enter Mobile"
          :state="$v.form.mobile.$dirty ? !$v.form.mobile.$error : null"
          data-cy="login-mobile"
          class="from-input input-mobile box-shadow3"
          :formatter="limit"
          :class="{
            'border-danger': !isDarkMode,
            'bg-dark text-light placeholder-light ': isDarkMode,
            'border-danger': errorMessages
          }"
        ></b-form-input>

        <b-form-text v-if="qmsOtpMode" :class="isDarkMode ? 'text-light' : 'text-muted'" class="mt-1">
          Use your QMS-registered mobile number.
        </b-form-text>

        <b-form-invalid-feedback
          id="input-mobile-invalid"
          data-cy="login-mobile-invalid"
          class="invalid-feedback invalid-feedback-mobile"
        >
          Please enter a valid 10-digit mobile number.
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group v-if="!qmsOtpMode" id="group-password" label label-for="input-password" description>
        <b-form-input
          id="input-password"
          type="password"
          v-model="form.password"
          required
          placeholder="Enter password"
          :state="$v.form.password.$dirty ? !$v.form.password.$error : null"
          data-cy="login-password"
          class="from-input input-password box-shadow3"
          :class="{
            'border-danger': !isDarkMode,
            'bg-dark text-light placeholder-light ': isDarkMode,
            'border-danger': errorMessages
          }"
        ></b-form-input>

        <b-form-invalid-feedback
          id="input-password-invalid"
          data-cy="login-password-invalid"
          class="invalid-feedback invalid-feedback-password"
        >
          Please enter your password.
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group v-else id="group-otp" label label-for="input-qms-otp" description>
        <b-form-input
          id="input-qms-otp"
          v-model="form.otp"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          placeholder="Enter OTP"
          :state="$v.form.otp.$dirty ? !$v.form.otp.$error : null"
          data-cy="login-qms-otp"
          class="from-input input-password box-shadow3"
          :class="{
            '': !isDarkMode,
            'bg-dark text-light placeholder-light ': isDarkMode
          }"
          :disabled="!qmsOtpSent"
        ></b-form-input>

        <b-form-text v-if="qmsOtpMode" :class="qmsOtpHelperClass" data-cy="login-qms-otp-helper">
          {{ qmsOtpHelperText }}
        </b-form-text>

        <b-form-invalid-feedback
          id="input-qms-otp-invalid"
          data-cy="login-qms-otp-invalid"
          class="invalid-feedback invalid-feedback-password"
        >
          Enter a valid OTP (4–6 digits).
        </b-form-invalid-feedback>
      </b-form-group>

      <template v-if="successMessages || errorMessages">
        <b-row class="mb-2">
          <b-col v-if="successMessages" data-cy="login-success-message" class="text-primary message-col">{{
            successMessages
          }}</b-col>
          <b-col v-if="errorMessages" data-cy="login-error-message" class="text-danger message-col">{{
            errorMessages
          }}</b-col>
        </b-row>
      </template>

      <b-row v-if="!qmsOtpMode">
        <b-col cols="12" class="mb-2">
          <b-button
            data-cy="login-button"
            class="box-shadow2 btn-login w-100"
            type="submit"
            :variant="isDarkMode ? 'outline-primary' : 'primary'"
            :disabled="$v.form.$invalid || loading"
          >
            <span class="spinner spinner-white" v-if="loading"></span>
            Login
          </b-button>
        </b-col>
      </b-row>
      <b-row v-if="!qmsOtpMode">
        <b-col lg="6" sm="12" class="mb-2">
          <b-button
            @click="goToPasswordChangeScreen"
            class="btn-login box-shadow3 w-100"
            :variant="isDarkMode ? 'outline-light' : 'light'"
          >
            Change Password
          </b-button>
        </b-col>
        <b-col lg="6" sm="12" class="mb-2">
          <b-button
            @click="goToPasswordResetScreen"
            class="btn-login box-shadow3 w-100"
            :variant="isDarkMode ? 'outline-light' : 'light'"
          >
            Reset Password
          </b-button>
        </b-col>
      </b-row>

      <b-row v-else>
        <b-col lg="6" sm="12" class="mb-2">
          <b-button
            type="button"
            class="box-shadow2 btn-login w-100"
            :variant="isDarkMode ? 'outline-primary' : 'primary'"
            :disabled="!canSendQmsOtp || qmsOtpSending || $v.form.mobile.$invalid"
            data-cy="qms-send-otp"
            @click="sendQmsOtp"
          >
            <span v-if="qmsOtpSending" class="spinner spinner-white"></span>
            {{ sendOtpLabel }}
          </b-button>
        </b-col>
        <b-col lg="6" sm="12" class="mb-2">
          <b-button
            type="button"
            class="box-shadow2 btn-login w-100"
            :variant="isDarkMode ? 'outline-success' : 'success'"
            :disabled="!qmsOtpSent || qmsOtpVerifying || $v.form.mobile.$invalid"
            data-cy="qms-verify-otp"
            @click="verifyQmsOtp"
          >
            <span v-if="qmsOtpVerifying" class="spinner spinner-white"></span>
            Verify OTP
          </b-button>
        </b-col>
        <b-col cols="12">
          <small v-if="cooldownLeft > 0" class="text-muted d-block">
            Resend available in {{ cooldownText }}. Next block: {{ nextCooldownMinute }} min
          </small>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { required, minLength, requiredIf } from 'vuelidate/lib/validators';
import router from '@/router';
import authService, { extractAuthKeyFromApiBody } from '@/services/authService';

import {
  QMS_MOBILE_STORAGE_KEY,
  clearQmsUserFromStorage,
  hasQmsUserData,
  readQmsUserFromStorage,
  saveQmsUserToStorage
} from '@/helper/qmsUserSession';

function qmsOtpFormat(value) {
  if (value === '' || value === null || value === undefined) return false;
  return /^\d{4,6}$/.test(String(value));
}

function qmsMobileFormat(value) {
  if (value === '' || value === null || value === undefined) return false;
  return /^\d{10}$/.test(String(value)) && !/^0{10}$/.test(String(value));
}

/** 422 body: { data: [{ msg, param, ... }] } */
function messageFromValidationResponse(e) {
  const res = e.response;
  if (!res || !res.data) {
    return e.message || 'Request failed';
  }
  const body = res.data;
  if (Array.isArray(body.data) && body.data.length) {
    const msgs = body.data.map(item => item && item.msg).filter(Boolean);
    if (msgs.length) {
      return msgs.join(' ');
    }
  }
  return body.message || body.error || e.message || 'Request failed';
}

export default {
  name: 'LoginBox',
  watch: {
    qmsOtpMode(enabled) {
      this.qmsApiError = '';
      this.qmsOtpSent = false;
      if (enabled) {
        this.form.password = '';
      } else {
        this.form.otp = '';
      }
      this.$nextTick(() => {
        this.$v.$reset();
      });
    },
    'form.mobile': function onFormMobileChange(newValue) {
      this.qmsApiError = '';
      this.qmsOtpSent = false;
      this.form.otp = '';
      if (newValue && this.qmsOtpMode) {
        localStorage.setItem(QMS_MOBILE_STORAGE_KEY, String(newValue));
      }
      const savedUser = readQmsUserFromStorage();
      if (savedUser && savedUser.employeeMobile && String(savedUser.employeeMobile) !== String(newValue || '')) {
        clearQmsUserFromStorage();
      }
    },
    'form.otp': function onFormOtpChange() {
      this.qmsApiError = '';
    }
  },
  data() {
    return {
      form: {
        mobile: '',
        password: '',
        otp: ''
      },
      qmsOtpMode: false,
      qmsOtpSent: false,
      qmsApiError: '',
      qmsOtpSending: false,
      qmsOtpVerifying: false,
      qmsSendAttempt: 0,
      cooldownLeft: 0,
      cooldownTimer: null
    };
  },
  validations() {
    return {
      form: {
        mobile: {
          required,
          qmsMobileOk(value) {
            return qmsMobileFormat(value);
          }
        },
        password: {
          required: requiredIf(() => !this.qmsOtpMode),
          minLength: minLength(6)
        },
        otp: {
          qmsOtpOk(value) {
            if (!this.qmsOtpMode) return true;
            return qmsOtpFormat(value);
          }
        }
      }
    };
  },
  mounted() {
    const savedUser = readQmsUserFromStorage();
    const savedQmsMobile =
      localStorage.getItem(QMS_MOBILE_STORAGE_KEY) || (savedUser && savedUser.employeeMobile) || '';
    if (savedQmsMobile) {
      this.form.mobile = savedQmsMobile;
    }
    if (this.isLoggedIn) {
      // Already logged in
      this.logout({ router, silent: true });
    }
  },
  beforeDestroy() {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
    }
  },
  computed: {
    ...mapGetters('alert', ['errorMessages', 'successMessages']),
    ...mapState('auth', ['loading']),
    ...mapState('darkMode', ['isDarkMode']),
    ...mapState('qms', ['qmsUserFromHono']),
    ...mapGetters('auth', ['isLoggedIn']),
    canSendQmsOtp() {
      return this.cooldownLeft <= 0;
    },
    sendOtpLabel() {
      if (this.cooldownLeft > 0) {
        return `Send OTP (${this.cooldownText})`;
      }
      return 'Send OTP';
    },
    qmsOtpHelperText() {
      if (this.qmsApiError) {
        return this.qmsApiError;
      }
      if (this.qmsOtpSent) {
        return 'Please enter the OTP to continue.';
      }
      return 'Enter a valid 10-digit mobile number to receive OTP.';
    },
    qmsOtpHelperClass() {
      if (this.qmsApiError) {
        return 'text-danger';
      }
      return this.isDarkMode ? 'text-light' : 'text-muted';
    },
    cooldownText() {
      const minutes = Math.floor(this.cooldownLeft / 60);
      const seconds = this.cooldownLeft % 60;
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    },
    nextCooldownMinute() {
      return Math.min(this.qmsSendAttempt + 1, 5);
    }
  },
  methods: {
    ...mapActions('auth', ['login', 'logout']),
    ...mapActions('qms', ['getOneQmsUserFromHono']),
    limit(value) {
      return String(value || '')
        .replace(/\D/g, '')
        .slice(0, 10);
    },
  openPfEsiHelpdesk() {
    window.open("https://pfesihelpdesk.aparajitha.com/home", "_blank");
  },
    isNotFoundError(error) {
      return error && error.response && error.response.status === 404;
    },
    async getOrCreateQmsUser(employeeMobile) {
      const userId = String(employeeMobile || '').trim();
      if (!userId) {
        return null;
      }

      return this.getOneQmsUserFromHono({
        id: userId,
        router: this.$router,
        silent: true
      });
    },
    onSubmit() {
      if (this.qmsOtpMode) {
        return;
      }
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }

      this.login({ mobile: this.form.mobile, password: this.form.password, router });
    },
    goToPasswordChangeScreen() {
      router.push(`/change-password`);
    },
    goToPasswordResetScreen() {
      router.push(`/reset-password`);
    },
    startQmsCooldown() {
      const waitMinute = Math.min(this.qmsSendAttempt, 5);
      this.cooldownLeft = waitMinute * 60;
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
      }

      this.cooldownTimer = setInterval(() => {
        if (this.cooldownLeft > 0) {
          this.cooldownLeft -= 1;
          return;
        }

        clearInterval(this.cooldownTimer);
        this.cooldownTimer = null;
      }, 1000);
    },
    async sendQmsOtp() {
      this.$v.form.mobile.$touch();
      if (this.$v.form.mobile.$invalid || !this.canSendQmsOtp) {
        return;
      }

      this.qmsOtpSending = true;
      this.qmsApiError = '';
      try {
        const qmsUser = await this.getOrCreateQmsUser(this.form.mobile);
        if (!hasQmsUserData(qmsUser)) {
          clearQmsUserFromStorage();
          this.qmsOtpSent = false;
          this.qmsApiError = 'User not found for this mobile number.';
          return;
        }

        saveQmsUserToStorage(qmsUser);
        await authService.generateQmsOtp({ employeeMobile: this.form.mobile });
        this.qmsSendAttempt += 1;
        this.qmsOtpSent = true;
        localStorage.setItem(QMS_MOBILE_STORAGE_KEY, this.form.mobile);
        this.startQmsCooldown();
        this.$store.dispatch(
          'alert/success',
          { showType: 'toast', title: 'OTP sent successfully', text: 'Please enter the OTP to continue.' },
          { root: true }
        );
      } catch (e) {
        this.qmsOtpSent = false;
        this.qmsApiError = this.isNotFoundError(e)
          ? 'User not found for this mobile number.'
          : messageFromValidationResponse(e);
      } finally {
        this.qmsOtpSending = false;
      }
    },
    async verifyQmsOtp() {
      this.$v.form.mobile.$touch();
      this.$v.form.otp.$touch();
      if (this.$v.form.mobile.$invalid || this.$v.form.otp.$invalid) {
        return;
      }

      this.qmsOtpVerifying = true;
      this.qmsApiError = '';
      try {
        const body = await authService.verifyQmsOtp({
          employeeMobile: this.form.mobile,
          otp: this.form.otp
        });
        this.qmsApiError = '';
        const authKey = extractAuthKeyFromApiBody(body);
        if (authKey) {
          try {
            this.$store.commit('auth/loginSuccess', { authKey });
          } catch (_e) {
            this.qmsApiError = 'Could not start session from server response.';
            return;
          }
        }
        sessionStorage.setItem('qmsOtpVerified', 'true');
        localStorage.setItem(QMS_MOBILE_STORAGE_KEY, String(this.form.mobile));
        this.$store.dispatch(
          'alert/success',
          { showType: 'toast', title: 'OTP verified', text: 'Opening QMS.' },
          { root: true }
        );
        router.push({
          path: '/qms/',
          query: {
            mobile: String(this.form.mobile || '')
          }
        });
      } catch (e) {
        this.qmsApiError = messageFromValidationResponse(e);
      } finally {
        this.qmsOtpVerifying = false;
      }
    }
  }
};
</script>

<style scoped>
.gap-8 {
  gap: 8px;
}
</style>
